import React, { useEffect, useState } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import { Smartphone } from 'lucide-react';

const DEFAULT_ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.gmina.app';

export default function AppVersionConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    latest_version: '1.0.0',
    min_required_version: '1.0.0',
    store_url_android: DEFAULT_ANDROID_URL,
    store_url_ios: '',
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('app_version_config')
        .select('latest_version, min_required_version, store_url_android, store_url_ios')
        .eq('id', 1)
        .single();

      if (error) throw error;
      if (data) {
        setForm({
          latest_version: data.latest_version || '1.0.0',
          min_required_version: data.min_required_version || '1.0.0',
          store_url_android: data.store_url_android || DEFAULT_ANDROID_URL,
          store_url_ios: data.store_url_ios || '',
        });
      }
    } catch (err) {
      console.error('Error loading app version config:', err);
      toast.error('Błąd ładowania konfiguracji wersji');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabaseAdmin
        .from('app_version_config')
        .update({
          latest_version: (form.latest_version || '').trim() || '1.0.0',
          min_required_version: (form.min_required_version || '').trim() || '1.0.0',
          store_url_android: (form.store_url_android || '').trim() || null,
          store_url_ios: (form.store_url_ios || '').trim() || null,
        })
        .eq('id', 1);

      if (error) throw error;
      toast.success('Konfiguracja wersji zapisana');
    } catch (err) {
      console.error('Error saving app version config:', err);
      toast.error('Błąd zapisu konfiguracji');
    } finally {
      setSaving(false);
    }
  };

  const forceUpdateActive = form.latest_version && form.min_required_version &&
    form.latest_version === form.min_required_version;

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Ładowanie konfiguracji wersji...</p>
      </div>
    );
  }

  return (
    <div className="manage-tab-panel" style={{ maxWidth: '560px' }}>
      <h1 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Smartphone size={28} />
        Wersja aplikacji
      </h1>
      <p style={{ marginBottom: '24px', color: '#666', fontSize: '15px' }}>
        Ustaw aktualną wersję w sklepie i minimalną wymaganą wersję. Użytkownicy z wersją niższą niż minimalna zobaczą blokujący ekran z prośbą o aktualizację.
      </p>

      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Aktualna wersja w sklepie (latest_version)
            </label>
            <input
              type="text"
              value={form.latest_version}
              onChange={(e) => handleChange('latest_version', e.target.value)}
              placeholder="np. 1.2.0"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '16px',
              }}
            />
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#666' }}>
              Wersja obecnie opublikowana w Play Store / App Store.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Minimalna wymagana wersja (min_required_version)
            </label>
            <input
              type="text"
              value={form.min_required_version}
              onChange={(e) => handleChange('min_required_version', e.target.value)}
              placeholder="np. 1.0.0"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '16px',
              }}
            />
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#666' }}>
              Użytkownicy z wersją <strong>niższą</strong> niż ta nie będą mogli korzystać z aplikacji (wymuszona aktualizacja).
              <br />
              <strong>Wymuszanie włączone:</strong> ustaw tę samą wartość co „Aktualna wersja”.
              <strong> Bez wymuszania:</strong> zostaw niższą wartość (np. 1.0.0), aby starsze wersje nadal działały.
            </p>
            {forceUpdateActive && (
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#c2410c', fontWeight: '500' }}>
                Obecnie wymuszanie aktualizacji jest włączone (obie wersje równe).
              </p>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Link do sklepu – Android
            </label>
            <input
              type="url"
              value={form.store_url_android}
              onChange={(e) => handleChange('store_url_android', e.target.value)}
              placeholder={DEFAULT_ANDROID_URL}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '16px',
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Link do sklepu – iOS (opcjonalnie)
            </label>
            <input
              type="url"
              value={form.store_url_ios}
              onChange={(e) => handleChange('store_url_ios', e.target.value)}
              placeholder="https://apps.apple.com/app/id..."
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '16px',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '10px 24px',
              backgroundColor: saving ? '#999' : '#0E2A47',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </form>
      </div>
    </div>
  );
}
